import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { authService, User, LoginResponse } from "../services/authService";

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => void;
  clearError: () => void;
  checkTokenExpiration: () => boolean;
}

// Função para decodificar JWT e verificar expiração
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp * 1000; // Converter para milliseconds
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);
          set({
            token: response.token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
          return response;
        } catch (error: any) {
          const message = error.response?.data?.error || "Erro ao fazer login";
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
        });
        // Limpa também o sessionStorage manualmente para garantir
        sessionStorage.removeItem("auth-storage");
      },

      clearError: () => set({ error: null }),

      // Verifica se o token expirou e faz logout se necessário
      checkTokenExpiration: () => {
        const { token, isAuthenticated } = get();
        if (isAuthenticated && isTokenExpired(token)) {
          get().logout();
          return false;
        }
        return isAuthenticated;
      },
    }),
    {
      name: "auth-storage",
      // Usa sessionStorage - sessão expira ao fechar o navegador
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Ao rehidratar, verifica se o token ainda é válido
      onRehydrateStorage: () => (state) => {
        if (state && state.token && isTokenExpired(state.token)) {
          // Token expirado, limpa o estado
          state.logout();
        }
      },
    }
  )
);
